import type {
  DateBound,
  DateFilter,
  MatchListValue,
  NumberPredicate,
  TreeConditionNode,
  TreeGroupNode,
  TreeNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { isGroupNode } from '@/components/tree-dnd/tree-dnd-types';
import type {
  DateBoundDto,
  DateFilterDto,
  FilterNodeDto,
  ItemsFilterDto,
  NumberFilterDto,
} from '@/generated/korza-api-client/types.gen';

export class AudienceFilterReverseMappingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AudienceFilterReverseMappingError';
  }
}

function newId(): string {
  return crypto.randomUUID();
}

function mapItemsFilter(items: ItemsFilterDto): MatchListValue {
  return { match: items.mode, ids: items.ids };
}

function mapNumberFilter(value: NumberFilterDto): NumberPredicate {
  return { kind: value.type, value: value.value };
}

function mapDateBound(bound: DateBoundDto): DateBound {
  if (bound.type === 'relative') {
    return {
      type: 'relative',
      offset: bound.offset,
      unit: bound.unit,
      timezone: bound.timezone,
    };
  }
  if (bound.type === 'absolute') {
    return { type: 'absolute', date: bound.date };
  }
  return { type: 'epoch' };
}

function mapDateFilter(date: DateFilterDto): DateFilter {
  switch (date.type) {
    case 'anytime':
      return { type: 'anytime' };
    case 'absolute':
      return {
        type: 'absolute',
        direction: date.direction,
        date: date.date,
      };
    case 'relative':
      return {
        type: 'relative',
        direction: date.direction,
        date: mapDateBound(date.date),
      };
    case 'window':
      return {
        type: 'window',
        from: mapDateBound(date.from),
        to: mapDateBound(date.to),
      };
    default: {
      const _exhaustive: never = date;
      throw new AudienceFilterReverseMappingError(
        `Unsupported date filter: ${JSON.stringify(_exhaustive)}`
      );
    }
  }
}

function mapConditionDto(
  dto: Extract<FilterNodeDto, { type: 'condition' }>
): TreeConditionNode {
  const base = {
    id: newId(),
    type: 'condition' as const,
    negated: false,
  };

  switch (dto.kind) {
    case 'appDownloadDate':
      return {
        ...base,
        kind: 'installedApp',
        args: { when: mapDateFilter(dto.params.date) },
      };
    case 'boughtProducts':
      return {
        ...base,
        kind: 'boughtProduct',
        args: {
          products: mapItemsFilter(dto.params.products),
          when: mapDateFilter(dto.params.date),
        },
      };
    case 'receivedCampaign':
      return {
        ...base,
        kind: 'receivedNotification',
        args: { campaigns: mapItemsFilter(dto.params.campaigns) },
      };
    case 'clickedCampaign':
      return {
        ...base,
        kind: 'clickedCampaign',
        args: { campaigns: mapItemsFilter(dto.params.campaigns) },
      };
    case 'lastNotificationAt':
      return {
        ...base,
        kind: 'lastNotificationAt',
        args: { when: mapDateFilter(dto.params.date) },
      };
    case 'lastAppAccessAt':
      return {
        ...base,
        kind: 'lastAppAccessAt',
        args: { when: mapDateFilter(dto.params.date) },
      };
    case 'viewedProduct':
      return {
        ...base,
        kind: 'viewedProduct',
        args: {
          products: mapItemsFilter(dto.params.products),
          when: mapDateFilter(dto.params.date),
        },
      };
    case 'lastOrder':
      return {
        ...base,
        kind: 'lastOrder',
        args: { when: mapDateFilter(dto.params.date) },
      };
    case 'orderFrequency':
      return {
        ...base,
        kind: 'orderFrequency',
        args: {
          count: mapNumberFilter(dto.params.count),
          when: mapDateFilter(dto.params.date),
        },
      };
    case 'totalSpend':
      return {
        ...base,
        kind: 'totalSpend',
        args: { amount: mapNumberFilter(dto.params.amount) },
      };
    case 'hasNotificationsEnabled':
      return {
        ...base,
        kind: 'hasNotificationsEnabled',
        args: {},
      };
    default: {
      const _exhaustive: never = dto;
      throw new AudienceFilterReverseMappingError(
        `Unsupported filter condition: ${JSON.stringify(_exhaustive)}`
      );
    }
  }
}

function applyNegation(node: TreeNode, negated: boolean): TreeNode {
  if (!negated) return node;
  return { ...node, negated: true };
}

function mapDtoNode(dto: FilterNodeDto, negated = false): TreeNode {
  if (dto.type === 'not') {
    return mapDtoNode(dto.child, !negated);
  }

  if (dto.type === 'and' || dto.type === 'or') {
    return applyNegation(
      {
        id: newId(),
        type: 'group',
        operator: dto.type === 'and' ? 'AND' : 'OR',
        negated: false,
        children: dto.children.map((child) => mapDtoNode(child)),
      },
      negated
    );
  }

  return applyNegation(mapConditionDto(dto), negated);
}

/** Map an API FilterNodeDto to a UI tree-dnd root group. */
export function mapFilterDtoToTree(dto: FilterNodeDto): TreeGroupNode {
  const node = mapDtoNode(dto);

  if (isGroupNode(node)) {
    return { ...node, id: 'root' };
  }

  return {
    id: 'root',
    type: 'group',
    operator: 'AND',
    negated: false,
    children: [node],
  };
}
